import { equals, find, includes } from 'ramda';

import {
    getFirstAvailableSSOPrimaryProof,
    isNeutralPagePath,
    isNotAuthenticatedPagePath,
    isOnboardingPagePath
} from '@framework/accounts.utils';
import { djangoRedirectorPath } from '@framework/constants/routes';
import { type Handler } from '@framework/server/middleware';
import { serverUrlBuilder } from '@framework/server/utils';
import { sanitizeNextUrl } from '@framework/utils/redirect';

import { api } from '@accounts/constants/endpoints';
import { type AuthenticationStatusResponse, type SSOMethod } from '@accounts/types/authentication';

export const fetchInitialAuthenticationStatus: Handler<[], void> = async (
    _ctx,
    { axiosServer, setState }
) => {
    const authUrl = serverUrlBuilder(api.authenticationStatus());
    const authenticationStatus = await axiosServer.get<AuthenticationStatusResponse>(authUrl);

    const csrftokenCookie = find(
        includes('csrftoken='),
        authenticationStatus.headers['set-cookie'] ?? []
    );
    const csrftoken = csrftokenCookie?.replace(/(csrftoken=)|(;.+)/g, '');

    /**
     * Override axiosServer instance's X-CSRFToken header to make sure that even new session
     * requests that initially don't have ctx.req.cookies.csrftoken can have it as well after
     * initial authentication status request.
     */
    // eslint-disable-next-line no-param-reassign
    axiosServer.defaults.headers.common = {
        ...axiosServer.defaults.headers.common,
        'X-CSRFToken': csrftoken as string
    };

    setState({
        authenticationStatus: authenticationStatus.data.proofStatus
    });
};

const authenticationGuards: Handler<[], void> = async (ctx, { getState }) => {
    if (!ctx.res || !ctx.req) {
        return;
    }

    const nextParam = ctx.query.next as string;
    const shouldForceAuthenticate = ctx.query.forceAuthenticate === 'true';
    const { headers, url, _parsedUrl } = ctx.req;
    const host = `https://${headers['x-forwarded-host']}`;
    const { authenticationStatus } = getState();

    if (!authenticationStatus) {
        return;
    }

    const { isAuthenticated } = authenticationStatus;

    const isNotAuthenticatedPage = isNotAuthenticatedPagePath(ctx.pathname);
    const isRootPage = equals(ctx.pathname, '/');
    const isNeutralPage = isNeutralPagePath(ctx.pathname);
    const isOnboardingPage = isOnboardingPagePath(ctx.pathname);
    const isErrorPage = equals(ctx.pathname, '/_error');

    const firstAvailableSSOPrimaryProof = getFirstAvailableSSOPrimaryProof(authenticationStatus);

    // Only redirect to the login page if it's an authenticated page and not nextjs error page
    const shouldRedirectToLoginPage =
        !isNotAuthenticatedPage && !isNeutralPage && !isErrorPage && !isAuthenticated;
    const shouldRedirectToAuthenticatedPage =
        isRootPage && isAuthenticated && !shouldForceAuthenticate;
    const shouldRedirectToIdPProvider =
        (isRootPage || isOnboardingPage) && !isAuthenticated && firstAvailableSSOPrimaryProof;

    // First check if the user needs to be redirected based on visited URL and user's authentication status
    let redirectPath: string | null = null;
    const nextPath = nextParam ? sanitizeNextUrl(nextParam, host) : djangoRedirectorPath;
    if (shouldRedirectToLoginPage) {
        // If the user isn't authenticated, append attempted URL to 'next' query param
        redirectPath = `/?next=${url}`;
    } else if (shouldRedirectToAuthenticatedPage) {
        /**
         * If there's a `next` query parameter, go there instead. Otherwise,
         * redirect to the authenticated page, this url is currently handled by BE
         */
        redirectPath = nextPath;
    } else if (shouldRedirectToIdPProvider) {
        // If all those don't check, then redirect to IdP URL + any URL parameter, including `next`
        let params = _parsedUrl.search || '';

        if (isOnboardingPage && ctx.asPath) {
            const onboardingParams = new URLSearchParams();
            onboardingParams.append('next', ctx.asPath);
            params = `?${onboardingParams.toString()}`;
        }

        redirectPath = `${(firstAvailableSSOPrimaryProof as SSOMethod).url}${params}`;
    }

    /**
     * If redirectPath is non null, then node should send a 302 response
     * and redirect the user to redirectPath
     */
    if (redirectPath) {
        ctx.res.redirect(redirectPath);
    }
};

export default authenticationGuards;
